import { useMemo, useState } from 'react';
import { Combobox, InlineStack, Select } from '@customerio/pluma-components/react';
import { fieldError } from '@customerio/dryad';
import { useDebouncedValue } from '@/hooks/state/use-debounced-value';
import { useEventConditionNameSearch } from '@/services/db/event-name/queries';
import { useEnvironmentOptional } from '@/services/db/environment/queries';
import { requireWorkspaceId } from '@/services/db/shared/workspace';
import { peopleFilterBuilder } from './builder';
import { ConditionType } from './condition-types';
import { ConditionTypeSelect } from './condition-type-select';
import { EventNameMatchingTooltip } from './event-name-matching-tooltip';
import { escapedEventNameOptions, mergeEventOptions } from './event-name-options';

type EventRowProps = {
  /** The leaf node's id, supplied by the group to every registered view. */
  id: string;
};

const INVERSE_OPTIONS = [
  { value: 'performed', label: 'has been performed' },
  { value: 'not-performed', label: 'has not been performed' },
];

/**
 * A behavioral event condition row: [type] [event name] [has (not) been
 * performed]. The state's `eventType`/`human` fields have no controls: they
 * only ride along so a parsed `p-` page bitmap or `_h`-suffixed name
 * re-serializes byte-identically.
 */
export function EventRow({ id }: EventRowProps) {
  const workspaceId = requireWorkspaceId();
  const meta = peopleFilterBuilder.useMeta();
  const { state, node } = peopleFilterBuilder.useLeafState(id, ConditionType.Event);
  const { updateState } = peopleFilterBuilder.useBuilderActions();
  const errors = peopleFilterBuilder.useNodeErrors(id);
  const environment = useEnvironmentOptional();

  // The combobox's live input text, kept local so keystrokes filter
  // suggestions without a builder round-trip.
  const [inputValue, setInputValue] = useState(() => state.eventName);
  const [debouncedTerm] = useDebouncedValue(inputValue, 200);
  const search = useEventConditionNameSearch(workspaceId, debouncedTerm);

  // "Event name can't be blank." renders only once the input has been visited
  // (blur) — or from the start when the row mounted with a name already set —
  // so a freshly added row doesn't open covered in red.
  const [eventNameTouched, setEventNameTouched] = useState(() => state.eventName !== '');
  const eventNameErrorMessage = eventNameTouched ? fieldError(errors, 'eventName') : undefined;

  const searchableEvents = useMemo(() => {
    const local = escapedEventNameOptions(environment.data?.environment?.sent_events ?? []);
    const remote = escapedEventNameOptions(search.data ?? []);
    return mergeEventOptions(local, remote, inputValue);
  }, [environment.data, search.data, inputValue]);

  const setEventName = (eventName: string) => {
    setInputValue(eventName);
    if (node != null) {
      updateState(node, { eventName });
    }
  };

  return (
    <InlineStack data-testid="dryad-condition-row" gap="050" alignBlock="baseline" wrap>
      <ConditionTypeSelect nodeId={id} currentType={ConditionType.Event} />
      <EventNameMatchingTooltip eventName={state.eventName}>
        <Combobox
          data-testid="dryad-behavioral-event-name"
          ariaLabel="Event name"
          size="sm"
          inputValue={inputValue}
          selectedValue={state.eventName === '' ? null : state.eventName}
          onValueChange={setEventName}
          onSelectionChange={(value: string | null) => {
            if (value != null) {
              setEventName(value);
            }
          }}
          onBlur={() => setEventNameTouched(true)}
          options={searchableEvents}
          placeholder="Select event"
          error={eventNameErrorMessage}
          withVirtualizer
          shouldFilter={false}
          isDisabled={meta.isDisabled}
        />
      </EventNameMatchingTooltip>
      <Select
        data-testid="dryad-behavioral-event-inverse-select"
        ariaLabel="Event occurrence"
        size="sm"
        options={INVERSE_OPTIONS}
        value={state.inverse ? 'not-performed' : 'performed'}
        onChange={(value: string | null) => {
          if (value != null && node != null) {
            updateState(node, { inverse: value === 'not-performed' });
          }
        }}
        isDisabled={meta.isDisabled}
      />
    </InlineStack>
  );
}
